import {Col, Row} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {BaseModal} from 'components/modals';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import Container from 'react-bootstrap/Container';
import React, {useEffect, useState} from 'react';
import Icon from '@/components/icons/Icon';
import {useStoreActions, useStoreState} from 'easy-peasy';
import axios, {AxiosResponse} from 'axios';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {buildEventDetailURL} from '@/utils/urlBuilder';
import {produce} from 'immer';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import {FormattedDateTime} from '@/components/utils/FormattedDateTime';
import {useAlert} from '@/components/alerts';
import TableComponent from '@/components/table/Table';
import i18n from 'i18n';
import {Tooltip} from 'components/tooltips';
import {extractErrorMessage} from '@/utils/errorHandling';

interface QualityAssuranceDataReportItem {
    id: number;
    datetime: string;
    lang: string;
    anonymizedData: boolean;
    channel: {
        name: string;
    };
    eventCallChannels?: string;
    eventCallDuration?: number;
    eventCallTransStatus?: string;
    eventChatFirstMessage?: string;
    eventEmailSubject?: string;
    eventFeedbackFirstQuestion?: string;
}

interface QualityAssuranceDataReportResponse {
    qualityAssuranceDataReport: QualityAssuranceDataReportItem[];
    pagesList: number[];
}

interface QualityAssuranceModalProps {
    modalOpen: boolean;
    closeModal: () => void;
    reportType: string;
    reportName: string;
    eventType: string;
    date: string;
    dateType: string;
}

interface StoreState {
    qualityAssuranceDataReport: {
        items: QualityAssuranceDataReportItem[];
        searchCurrentPage: number;
        searchPagesList: number[];
        loading: boolean;
    };
}

interface StoreActions {
    qualityAssuranceDataReport: {
        loadQualityAssuranceDataReport: (data: QualityAssuranceDataReportItem[]) => void;
        setSearchCurrentPage: (page: number) => void;
        setSearchPagesList: (pages: number[]) => void;
        setLoading: (loading: boolean) => void;
    };
}

const qualityAssuranceDataReportUpdateRequest = (url: string, paramsDict: Record<string, any>): Promise<AxiosResponse<QualityAssuranceDataReportResponse>> =>
    axios.get(url, {params: paramsDict})
        .catch((error) => {
            if (error.response?.status === 400) {
                return error.response;
            }
            throw error;
        });

const qualityAssuranceDataReportUpdateRequestDebounced = AwesomeDebouncePromise(qualityAssuranceDataReportUpdateRequest, 350);

const QualityAssuranceModal: React.FC<QualityAssuranceModalProps> = React.memo((
    {
        modalOpen,
        closeModal,
        reportType,
        reportName,
        eventType,
        date,
        dateType,
    },
) => {
    const {showSuccess, showError} = useAlert();
    const qualityAssuranceDataReport = useStoreState((state: StoreState) => state.qualityAssuranceDataReport.items);
    const storeQualityAssuranceDataReport = useStoreActions((actions: StoreActions) => actions.qualityAssuranceDataReport.loadQualityAssuranceDataReport);

    const searchCurrentPage = useStoreState((state: StoreState) => state.qualityAssuranceDataReport.searchCurrentPage);
    const storeSearchCurrentPage = useStoreActions((actions: StoreActions) => actions.qualityAssuranceDataReport.setSearchCurrentPage);

    const searchPagesList = useStoreState((state: StoreState) => state.qualityAssuranceDataReport.searchPagesList);
    const storeSearchPagesList = useStoreActions((actions: StoreActions) => actions.qualityAssuranceDataReport.setSearchPagesList);

    const loadingQualityAssuranceDataReport = useStoreState((state: StoreState) => state.qualityAssuranceDataReport.loading);
    const setLoadingQualityAssuranceDataReport = useStoreActions((actions: StoreActions) => actions.qualityAssuranceDataReport.setLoading);

    const [actionModalOpen, setActionModalOpen] = useState<boolean>(false);

    // Reset pagination when modal opens with new parameters
    useEffect(() => {
        if (modalOpen && reportType !== '0') {
            storeSearchCurrentPage(1);
        }
    }, [modalOpen, reportType, eventType, date, dateType, storeSearchCurrentPage]);

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            if (eventType) {
                setLoadingQualityAssuranceDataReport(true);

                const paramsDict = {
                    page: searchCurrentPage,
                };

                const response = await qualityAssuranceDataReportUpdateRequestDebounced(
                    (window as any).URLS.qualityAssuranceDataReportResource.replace(':event_type', eventType).replace(':report_type', reportType).replace(':date', date).replace(':date_type', dateType), paramsDict,
                );

                storeQualityAssuranceDataReport(response.data.qualityAssuranceDataReport);
                storeSearchPagesList(response.data.pagesList);

                setLoadingQualityAssuranceDataReport(false);
            }
        };

        if (reportType !== '0') {
            fetchData();
        }
    }, [
        searchCurrentPage,
        storeSearchPagesList,
        storeQualityAssuranceDataReport,
        setLoadingQualityAssuranceDataReport,
        eventType,
        reportType,
        date,
        dateType,
    ]);

    const openActionModal = (): void => {
        setActionModalOpen(true);
    };

    const closeActionModal = (): void => {
        setActionModalOpen(false);
    };

    const onModalDiscard = (): void => {
        setActionModalOpen(false);
    };

    const onActionApprove = async (): Promise<void> => {
        setActionModalOpen(false);

        const postData = {
            report_type: reportType,
            event_type: eventType,
            date,
            date_type: dateType,
        };

        try {
            await axios.post((window as any).URLS.qualityAssuranceDataReportResource.replace(':event_type', eventType).replace(':report_type', reportType).replace(':date', date).replace(':date_type', dateType), postData);
            showSuccess(i18n.t('jt_action_execution.text'));
        } catch (error) {
            const errorMessage = extractErrorMessage(error, i18n.t('jt_action_execution.text'));
            showError(errorMessage);
            throw error;
        }
    };

    const pageChanged = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        if (e.currentTarget.textContent) {
            storeSearchCurrentPage(parseInt(e.currentTarget.textContent, 10));
        }
    };

    const handlePageChange = (page: number) => {
        storeSearchCurrentPage(page);
    };

    let columnInfo = [
        {
            accessorKey: 'datetime',
            header: i18n.t('th_datetime.text'),
            cell: (cell: any) => {
                if (cell.getValue() === '-') {
                    return cell.getValue();
                }

                return (
                    <FormattedDateTime
                        dateTime={cell.getValue()}
                        showTime={false}
                        options={{zone: 'utc'}}
                    />
                );
            },
        },
        {
            accessorKey: 'lang',
            header:  i18n.t('th_language.text'),
        },
        {
            accessorKey: 'anonymizedData',
            header: i18n.t('th_anonymised_data.text'),
            cell: (cell: any) => {
                if (cell.getValue() === true) {
                    return (<Icon name="mdi-check" className="text-success" />);
                } else {
                    return (<Icon name="mdi-close" className="text-danger" />);
                }
            },
        },
        {
            accessorKey: 'channel.name',
            header: i18n.t('th_channel.text'),
        },
    ];

    if (eventType === 'call') {
        columnInfo = produce(columnInfo, (draft) => {
            draft.push({
                accessorKey: 'eventCallChannels',
                header: i18n.t('th_speakers.text'),
            });
            draft.push({
                accessorKey: 'eventCallDuration',
                header: i18n.t('th_duration_sec.text'),
            });
            draft.push({
                accessorKey: 'eventCallTransStatus',
                header: i18n.t('th_trans_status.text'),
            });
        });
    } else if (eventType === 'chat') {
        columnInfo = produce(columnInfo, (draft) => {
            draft.push({
                accessorKey: 'eventChatFirstMessage',
                header: i18n.t('th_first_message.text'),
            });
        });
    } else if (eventType === 'email') {
        columnInfo = produce(columnInfo, (draft) => {
            draft.push({
                accessorKey: 'eventEmailSubject',
                header: i18n.t('th_subject.text'),
            });
        });
    } else if (eventType === 'feedback') {
        columnInfo = produce(columnInfo, (draft) => {
            draft.push({
                accessorKey: 'eventFeedbackFirstQuestion',
                header: i18n.t('th_first_question.text'),
            });
        });
    }

    columnInfo = produce(columnInfo, (draft) => {
        draft.push({
            accessorKey: 'actions',
            header: i18n.t('th_action.text'),
            cell: (cell: any) => (
                <div className="d-inline-block">
                    <a
                        href={buildEventDetailURL(cell.row.original.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{textDecoration: 'none'}}
                    >
                        <Tooltip content={i18n.t('jt_view.text')} placement="top">
                            <Button
                                variant="outline-info"
                                size="sm"
                                icon="mdi-eye"
                                data-cy="quality-assurance-eye-outlineinfo-btn" />
                        </Tooltip>
                    </a>
                </div>
            ),
        });
    });

    return (
        <>
            <BaseModal
                isOpen={modalOpen}
                onClose={closeModal}
                title={`${i18n.t('qa_data_report.text')} ${reportName}`}
                size="xl"
            >
                <Container>
                    <Row>
                        <Col md={12} className="m-auto">
                            <LoadingWrapper
                                loading={loadingQualityAssuranceDataReport}
                                loadingText={i18n.t('jt_loading_data_general.text')}
                                minHeight="300px"
                            >
                                <TableComponent data={qualityAssuranceDataReport} columns={columnInfo} />
                                <Row className="mt-2 d-flex justify-content-between align-items-center">
                                    <PaginationComponent
                                        pages={searchPagesList}
                                        currentPage={searchCurrentPage}
                                        onPageChange={handlePageChange}
                                    />
                                    {
                                        ['no_lang', 'no_anon', 'calls_without_duration', 'calls_without_compression', 'calls_with_transcription_error', 'calls_with_transcription_inserted'].includes(reportType) ? (
                                            <button
                                                type="button"
                                                className="btn btn-info"
                                                onClick={openActionModal}
                                                data-cy="quality-assurance-jtexecuteaction-text-btn">
                                                {i18n.t('jt_execute_action.text')}
                                            </button>
                                        ) : null
                                    }
                                </Row>
                            </LoadingWrapper>
                        </Col>
                    </Row>
                </Container>
            </BaseModal>
            <BaseModal
                isOpen={actionModalOpen}
                onClose={closeActionModal}
                title={i18n.t('jt_approve_action.text')}
                footer={
                    <>
                        <Button data-dismiss="alert" onClick={() => onModalDiscard()} variant="secondary">
                            {i18n.t('jt_discard.text')}
                        </Button>
                        <Button onClick={() => onActionApprove()} variant="info">
                            {i18n.t('jt_approve.text')}
                        </Button>
                    </>
                }
            >
                {i18n.t('jt_approving_action.text', {reportType: reportType})}
            </BaseModal>
        </>
    );
});

QualityAssuranceModal.displayName = 'QualityAssuranceModal';

export default QualityAssuranceModal; 